/**
 * Form controls.
 *
 * Almost all of these live inside modals and drawers, because configuration lives behind a
 * click. The two exceptions are SearchInput and FilterSelect, which are how an operator
 * narrows a list they are already looking at.
 *
 * Every control here is 36px tall with a 6px radius, matching the approved sheet, and every
 * one of them takes a `label` and a `hint`. A control with no label is a control whose
 * meaning lives in someone's head; a control with no hint is a control that will be asked
 * about. Both are cheap here and expensive at 2 AM.
 *
 * Switch, Checkbox and Radio are real buttons with ARIA roles rather than native inputs,
 * because the native ones cannot be styled to the sheet without a wrapper anyway, and a
 * half-native control is worse than an honest one. They are keyboard-operable and they
 * announce their state.
 */
import { forwardRef, useId } from 'react';
import type {
  InputHTMLAttributes,
  ReactElement,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import { Icon } from './Icon.tsx';
import type { IconName } from './Icon.tsx';
import { HelpTip } from './HelpTip.tsx';

// ------------------------------------------------------------------ field wrapper

export type FieldProps = {
  label?: ReactNode;
  /** The line under the control: the rule, the unit, what happens when you save. */
  hint?: ReactNode;
  /** Replaces the hint and turns the control red. */
  error?: string;
  helpKey?: string;
  /** Rendered beside the label, right-aligned — a "Locked" badge, a character count. */
  labelAside?: ReactNode;
  htmlFor?: string;
  children: ReactNode;
  className?: string;
};

export function Field({
  label,
  hint,
  error,
  helpKey,
  labelAside,
  htmlFor,
  children,
  className,
}: FieldProps): ReactElement {
  return (
    <div className={['nd-field', className].filter(Boolean).join(' ')}>
      {label ? (
        <label className="nd-label" htmlFor={htmlFor} style={labelAside ? { display: 'flex', alignItems: 'center' } : undefined}>
          {label}
          {helpKey ? <HelpTip helpKey={helpKey} /> : null}
          {labelAside ? <span className="nd-right">{labelAside}</span> : null}
        </label>
      ) : null}
      {children}
      {/* The id matches what the controls point their aria-describedby at. */}
      {error ? (
        <div className="nd-hint nd-error-text" id={htmlFor ? `${htmlFor}-hint` : undefined}>
          {error}
        </div>
      ) : hint ? (
        <div className="nd-hint" id={htmlFor ? `${htmlFor}-hint` : undefined}>
          {hint}
        </div>
      ) : null}
    </div>
  );
}

/** Two fields side by side, stacking on a phone. */
export function FieldRow({ children }: { children: ReactNode }): ReactElement {
  return <div className="nd-frow">{children}</div>;
}

// ------------------------------------------------------------------ text inputs

export type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> & {
  label?: ReactNode;
  hint?: ReactNode;
  error?: string;
  helpKey?: string;
  /** For ids, phone numbers, times, anything an operator reads back out loud. */
  mono?: boolean;
  /** Rendered inside the control on the right — a unit, a clock icon. */
  suffix?: ReactNode;
};

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, helpKey, mono, suffix, className, id, ...rest },
  ref,
): ReactElement {
  const autoId = useId();
  const inputId = id ?? autoId;

  const control = (
    <input
      {...rest}
      ref={ref}
      id={inputId}
      className={['nd-input', className].filter(Boolean).join(' ')}
      data-mono={mono ? 'true' : undefined}
      data-invalid={error ? 'true' : undefined}
      aria-invalid={error ? true : undefined}
      aria-describedby={hint || error ? `${inputId}-hint` : undefined}
    />
  );

  const wrapped = suffix ? (
    <span className="nd-search">
      {control}
      <span className="nd-search-clear" style={{ pointerEvents: 'none', color: 'var(--nd-ink-2)' }}>
        {suffix}
      </span>
    </span>
  ) : (
    control
  );

  if (!label && !hint && !error) return wrapped;
  return (
    <Field label={label} hint={hint} error={error} helpKey={helpKey} htmlFor={inputId}>
      {wrapped}
    </Field>
  );
});

export type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  label?: ReactNode;
  hint?: ReactNode;
  error?: string;
  helpKey?: string;
};

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, hint, error, helpKey, className, id, rows = 4, ...rest },
  ref,
): ReactElement {
  const autoId = useId();
  const areaId = id ?? autoId;
  const control = (
    <textarea
      {...rest}
      ref={ref}
      id={areaId}
      rows={rows}
      className={['nd-textarea', className].filter(Boolean).join(' ')}
      data-invalid={error ? 'true' : undefined}
      aria-invalid={error ? true : undefined}
      aria-describedby={hint || error ? `${areaId}-hint` : undefined}
    />
  );
  if (!label && !hint && !error) return control;
  return (
    <Field label={label} hint={hint} error={error} helpKey={helpKey} htmlFor={areaId}>
      {control}
    </Field>
  );
});

export type SelectOption = { value: string; label: string; disabled?: boolean };

export type SelectProps = Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children'> & {
  options: SelectOption[];
  label?: ReactNode;
  hint?: ReactNode;
  error?: string;
  helpKey?: string;
  /** Rendered first, with an empty value — "Choose a source…". */
  placeholder?: string;
  /**
   * Size the control to its own content rather than to the column it sits in. Use it where
   * the option labels are sentences and the column is narrow — a select that truncates the
   * setting an operator is choosing is worse than one that is wider than its neighbours.
   */
  fit?: boolean;
};

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { options, label, hint, error, helpKey, placeholder, className, id, fit = false, title, ...rest },
  ref,
): ReactElement {
  const autoId = useId();
  const selectId = id ?? autoId;

  /*
   * A native <select> clips its selected option at the glyph with no ellipsis — which is how
   * "Allowed — the assistant doe" and "Ask me first — it becomes a" shipped in a dialog
   * governing what customers are told. The stylesheet now ellipsises it; this makes the full
   * label reachable on hover and to a screen reader even when it is ellipsised.
   */
  const current = options.find((option) => option.value === rest.value)?.label;

  const control = (
    <select
      {...rest}
      ref={ref}
      id={selectId}
      title={title ?? current ?? placeholder}
      data-fit={fit ? 'true' : undefined}
      className={['nd-select', className].filter(Boolean).join(' ')}
      data-invalid={error ? 'true' : undefined}
      aria-invalid={error ? true : undefined}
      aria-describedby={hint || error ? `${selectId}-hint` : undefined}
    >
      {placeholder ? <option value="">{placeholder}</option> : null}
      {options.map((option) => (
        <option key={option.value} value={option.value} disabled={option.disabled}>
          {option.label}
        </option>
      ))}
    </select>
  );
  if (!label && !hint && !error) return control;
  return (
    <Field label={label} hint={hint} error={error} helpKey={helpKey} htmlFor={selectId}>
      {control}
    </Field>
  );
});

/**
 * The list-narrowing select that sits above a table. Same control, no label block, and it
 * shows its own name inside itself so a filtered list always says what it is filtered by.
 */
export function FilterSelect({
  label,
  value,
  options,
  onChange,
  allLabel = 'All',
  disabled,
}: {
  label: string;
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
  /** The empty-value option's label. Set to null to omit it. */
  allLabel?: string | null;
  disabled?: boolean;
}): ReactElement {
  const id = useId();
  return (
    <span className="nd-row" style={{ gap: 6 }}>
      <label className="nd-sr" htmlFor={id}>
        {label}
      </label>
      <select
        id={id}
        className="nd-select"
        style={{ width: 'auto', minWidth: 130, height: 'var(--nd-btn-h)' }}
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        title={label}
      >
        {allLabel === null ? null : <option value="">{`${label}: ${allLabel}`}</option>}
        {options.map((option) => (
          <option key={option.value} value={option.value} disabled={option.disabled}>
            {`${label}: ${option.label}`}
          </option>
        ))}
      </select>
    </span>
  );
}

export function SearchInput({
  value,
  onChange,
  placeholder = 'Search',
  label = 'Search',
  autoFocus,
  width,
  onSubmit,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** Accessible name. Required in spirit — a bare magnifier is not a label. */
  label?: string;
  autoFocus?: boolean;
  width?: number | string;
  onSubmit?: (value: string) => void;
}): ReactElement {
  const id = useId();
  return (
    <span className="nd-search" style={{ width: width ?? 240, flex: '0 0 auto' }}>
      <Icon name="search" size={14} />
      <label className="nd-sr" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className="nd-input"
        type="search"
        value={value}
        placeholder={placeholder}
        autoFocus={autoFocus}
        autoComplete="off"
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') onSubmit?.(value);
          if (event.key === 'Escape' && value) {
            event.stopPropagation();
            onChange('');
          }
        }}
      />
      {value ? (
        <button
          type="button"
          className="nd-iconbtn nd-search-clear"
          data-size="sm"
          aria-label="Clear the search"
          title="Clear"
          onClick={() => onChange('')}
        >
          <Icon name="close" size={13} />
        </button>
      ) : null}
    </span>
  );
}

// ------------------------------------------------------------------ toggles

export type SwitchProps = {
  checked: boolean;
  onChange: (next: boolean) => void;
  /** Required — a switch with no accessible name is a switch nobody can describe. */
  label: string;
  disabled?: boolean;
  /**
   * Enforced off (or on) for every tenant. The switch renders locked, states the rule in
   * its tooltip, and cannot be moved. Doctrine 1 and 2 both land here.
   */
  locked?: string;
};

export function Switch({ checked, onChange, label, disabled, locked }: SwitchProps): ReactElement {
  const isLocked = typeof locked === 'string';
  return (
    <button
      type="button"
      role="switch"
      className="nd-switch"
      aria-checked={checked}
      aria-label={label}
      title={locked ?? label}
      data-locked={isLocked ? 'true' : undefined}
      disabled={disabled || isLocked}
      onClick={() => {
        if (disabled || isLocked) return;
        onChange(!checked);
      }}
    />
  );
}

export function Checkbox({
  checked,
  onChange,
  label,
  disabled,
  indeterminate = false,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  disabled?: boolean;
  indeterminate?: boolean;
}): ReactElement {
  return (
    <button
      type="button"
      role="checkbox"
      className="nd-checkbox"
      aria-checked={indeterminate ? 'mixed' : checked}
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
    >
      <Icon name={indeterminate ? 'minus' : 'check'} size={11} />
    </button>
  );
}

export type RadioOption = {
  value: string;
  label: string;
  /** One line under the label: what choosing this actually does. */
  detail?: string;
  disabled?: boolean;
};

/** A vertical set of exclusive choices, each with room to explain itself. */
export function RadioGroup({
  name,
  value,
  options,
  onChange,
  label,
}: {
  name: string;
  value: string;
  options: RadioOption[];
  onChange: (value: string) => void;
  /** Accessible name for the whole group. */
  label: string;
}): ReactElement {
  return (
    <div role="radiogroup" aria-label={label}>
      {options.map((option) => (
        <div className="nd-optrow" key={option.value}>
          <button
            type="button"
            role="radio"
            className="nd-radio"
            name={name}
            aria-checked={option.value === value}
            aria-label={option.label}
            disabled={option.disabled}
            onClick={() => onChange(option.value)}
            style={{ marginTop: 1 }}
          />
          <span className="nd-optrow-text">
            <b>{option.label}</b>
            {option.detail ? <span>{option.detail}</span> : null}
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * The labelled row a Switch or Checkbox sits in — the shape the whole Announcement policy
 * modal is built from. `locked` renders the row muted with a Locked badge beside it, which
 * is the only honest way to show a setting that exists but cannot be changed.
 */
export function OptionRow({
  title,
  detail,
  control,
  locked = false,
  aside,
}: {
  title: ReactNode;
  detail?: ReactNode;
  control: ReactNode;
  locked?: boolean;
  /** Rendered between the text and the control — usually a LockBadge. */
  aside?: ReactNode;
}): ReactElement {
  return (
    <div className="nd-optrow" data-locked={locked ? 'true' : undefined}>
      <span className="nd-optrow-text">
        <b>{title}</b>
        {detail ? <span>{detail}</span> : null}
      </span>
      {aside}
      {control}
    </div>
  );
}

/**
 * A full-bleed row for a flush modal body — the system-health list. Dot, text, one action.
 */
export function ListRow({
  dot,
  title,
  detail,
  action,
}: {
  dot?: ReactNode;
  title: ReactNode;
  detail?: ReactNode;
  action?: ReactNode;
}): ReactElement {
  return (
    <div className="nd-listrow">
      {dot}
      <span className="nd-listrow-text">
        <b>{title}</b>
        {detail ? <span>{detail}</span> : null}
      </span>
      {action}
    </div>
  );
}

export type SegmentedOption = { value: string; label: ReactNode; icon?: IconName; disabled?: boolean };

/**
 * SegmentedControl — an exclusive choice with few enough options to show them all. Use it
 * for a view switch ("Tonight / Last 7 / Last 30"); use a Select once there are more than
 * four, and a RadioGroup once each choice needs a sentence.
 */
export function SegmentedControl({
  value,
  options,
  onChange,
  label,
}: {
  value: string;
  options: SegmentedOption[];
  onChange: (value: string) => void;
  label: string;
}): ReactElement {
  return (
    <div className="nd-segmented" role="radiogroup" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="radio"
          className="nd-segmented-item"
          aria-checked={option.value === value}
          disabled={option.disabled}
          onClick={() => onChange(option.value)}
        >
          {option.icon ? <Icon name={option.icon} size={13} /> : null}
          {option.label}
        </button>
      ))}
    </div>
  );
}

export type TabItem = { id: string; label: ReactNode; count?: number; disabled?: boolean };

/**
 * Tabs — the underlined row from the drawer header, usable anywhere. It renders the tab
 * strip only; the caller renders the panel, because a tab that has to lift its content into
 * this component is a tab that cannot lazy-load it.
 */
export function Tabs({
  items,
  active,
  onChange,
  label,
  className,
}: {
  items: TabItem[];
  active: string;
  onChange: (id: string) => void;
  label: string;
  className?: string;
}): ReactElement {
  return (
    <div className={['nd-tabs', className].filter(Boolean).join(' ')} role="tablist" aria-label={label}>
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          role="tab"
          className="nd-tab"
          aria-selected={item.id === active}
          disabled={item.disabled}
          onClick={() => onChange(item.id)}
        >
          {item.label}
          {item.count === undefined ? null : <span className="nd-tab-count">{item.count}</span>}
        </button>
      ))}
    </div>
  );
}

// ------------------------------------------------------------------ density

/**
 * ROW DENSITY — one preference, stored once, reachable from ⌘K.
 *
 * The measurement that earns it, taken off the live box at 1440×1100: every table row is a
 * fixed 48px, so a 900px table viewport shows 18 rows. At 36px the same laptop shows 25.
 * That is 39% more of the night visible without scrolling, on the machine that has the
 * least room — a 2 AM operator on a 13" laptop is exactly who is worst served by a row
 * height tuned on a 27" panel.
 *
 * Three decisions, all of them arguable, so all of them written down:
 *
 *   TWO settings, not three. "Comfortable" and "Compact". A third option is a menu, and a
 *   menu is a decision the product is refusing to make.
 *
 *   NO NEW CHROME. There is no density toggle in any table header and no segmented control
 *   in any toolbar. It lives in the command palette, which costs zero pixels and is already
 *   the answer to "where is the thing I use twice a year". Settings may adopt
 *   <DensityControl /> if it ever wants a visible home; nothing else should.
 *
 *   ROW HEIGHT ONLY. Density does not touch type size (that would be a zoom control
 *   wearing a density label), panel padding, or anything a figure is read inside of. It
 *   changes --nd-row-h, which the table cell, the skeleton row and the mobile stacked
 *   record all already derive from, so the skeleton stays the same shape as the content it
 *   becomes at either setting.
 *
 * It persists in localStorage and is applied to <html data-density> before first paint by
 * the module-load call below, so a reload does not flash 48px rows at someone who chose 36.
 */
export type Density = 'comfortable' | 'compact';

const DENSITY_KEY = 'nd-density';

export function getDensity(): Density {
  if (typeof window === 'undefined') return 'comfortable';
  try {
    return window.localStorage.getItem(DENSITY_KEY) === 'compact' ? 'compact' : 'comfortable';
  } catch {
    // Private mode, or storage disabled by policy. A preference that cannot be saved is not
    // an error worth showing anyone; the default is a good default.
    return 'comfortable';
  }
}

export function setDensity(next: Density): void {
  if (typeof document === 'undefined') return;
  if (next === 'compact') document.documentElement.setAttribute('data-density', 'compact');
  else document.documentElement.removeAttribute('data-density');
  try {
    window.localStorage.setItem(DENSITY_KEY, next);
  } catch {
    /* see getDensity */
  }
}

/** Applies the stored preference. Called at module load, and safe to call again. */
export function applyStoredDensity(): void {
  setDensity(getDensity());
}

if (typeof document !== 'undefined') applyStoredDensity();

/**
 * The optional visible control, for Settings. Nothing renders this today, and that is the
 * point — the mechanism is complete without it.
 */
export function DensityControl({ onChange }: { onChange?: (next: Density) => void }): ReactElement {
  const current = getDensity();
  return (
    <SegmentedControl
      label="Row density"
      value={current}
      onChange={(next) => {
        setDensity(next as Density);
        onChange?.(next as Density);
      }}
      options={[
        { value: 'comfortable', label: 'Comfortable' },
        { value: 'compact', label: 'Compact' },
      ]}
    />
  );
}
